import React, {memo, useMemo} from 'react';
import {AppBar, Link, Stack, Toolbar, Typography} from '@mui/material';
import {
  arrayOf,
  bool,
  func,
  node,
  object,
  oneOfType,
  shape,
  string,
} from 'prop-types';

import AppsMenu from './AppsMenu.jsx';
import NavDivider from './NavDivider.jsx';
import PlanetIcon from '../Logo/Icon.jsx';
import UserMenu from './UserMenu.jsx';
import {APPS, ENVIRONMENTS} from '../apps.jsx';

/**
 * Navigation bar for use as header in Planet apps
 */
export const NavBar = ({
  title,
  user,
  logout,
  login,
  secondary,
  appsMenuProps,
  userMenuProps,
  sx,
  environment = ENVIRONMENTS.staging,
  includeLegacySentinelHubApps,
  logoUrl,
  ...props
}) => {
  const showLegacySentinelHubApps = useMemo(
    () => !!user?.sh_user_id && includeLegacySentinelHubApps,
    [includeLegacySentinelHubApps, user?.sh_user_id],
  );

  return (
    <AppBar
      position="static"
      sx={[
        {
          zIndex: theme => theme.zIndex.drawer + 1,
          backgroundColor: 'background.paper',
          color: 'text.primary',
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...props}
    >
      <Toolbar variant="dense" disableGutters sx={{paddingX: 1}}>
        <Link
          href={logoUrl || APPS.welcomePage.href[environment]}
          underline="none"
          color="inherit"
        >
          <Stack
            direction="row"
            sx={{
              gap: 1,
              alignItems: 'center',
              paddingX: 1,
              paddingY: 0.75,
            }}
          >
            <PlanetIcon />
            <Typography
              variant="subtitle2"
              sx={{
                display: {
                  xs: 'none',
                  sm: 'inline',
                },
              }}
            >
              Insights Platform
            </Typography>
          </Stack>
        </Link>
        {title && <NavDivider style={{marginInline: 8}} />}
        {title && typeof title === 'string' ? (
          <Typography
            variant="subtitle2"
            sx={{paddingX: 1, paddingY: 0.75, color: 'text.primary'}}
          >
            {title}
          </Typography>
        ) : (
          title
        )}
        {title && <NavDivider style={{marginInline: 8}} />}

        <Stack
          direction="row"
          sx={{
            gap: 0.5,
            alignItems: 'center',
            marginLeft: 'auto',
          }}
        >
          {secondary}
          <AppsMenu
            environment={environment}
            includeLegacySentinelHubApps={showLegacySentinelHubApps}
          />
          {(user || login || logout) && (
            <UserMenu
              user={user}
              logout={logout}
              login={login}
              {...userMenuProps}
            />
          )}
        </Stack>
      </Toolbar>
    </AppBar>
  );
};

NavBar.propTypes = {
  /**
   App title
  */
  title: oneOfType([string, node]),

  /**
   Secondary area
  */
  secondary: oneOfType([arrayOf(node), node]),
  /**
   Logged in User for User Menu
  */
  user: shape({
    first_name: string,
    last_name: string,
    email: string,
    name: string,
    sh_user_id: string,
  }),
  /**
   Logout function for User Menu
  */
  logout: func,
  /**
   Login function for User Menu
  */
  login: func,
  /**
   Additional Props for the Apps Menu
  */
  appsMenuProps: object,
  /**
   Additional Props for the User Menu
  */
  userMenuProps: object,

  /**
   * Override NavBar styles
   */
  sx: object,

  /**
   * Environment to use for links
   */
  environment: string,

  includeLegacySentinelHubApps: bool,

  logoUrl: string,
};

// https://github.com/storybookjs/storybook/issues/26720
const MemoNavbar = memo(NavBar);
MemoNavbar.displayName = 'NavBar';

export default MemoNavbar;
